import { Header, Table } from '@tanstack/react-table';
import { useEffect, useState } from 'react';

export const getHeader = <T>(table: Table<T>, i: number) =>
  table.getHeaderGroups()?.[0].headers[i];

export const getColumnSizing = () =>
  JSON.parse(localStorage.getItem('columnSizing') || '{}') as {
    [key: string]: {
      columnSizing?: { [key: string]: number };
      width?: number;
    };
  };

export const saveColumnSizing = <T>(
  table: Table<T>,
  tableKey: string = 'songs'
) => {
  const state = table.getState();
  const columnSizing = getColumnSizing();
  if (!state.columnSizingInfo.isResizingColumn) {
    delete columnSizing[tableKey];
    localStorage.setItem('columnSizing', JSON.stringify(columnSizing));
    return;
  }

  localStorage.setItem(
    'columnSizing',
    JSON.stringify({
      ...columnSizing,
      [tableKey]: {
        columnSizing: {
          ...columnSizing[tableKey]?.columnSizing,
          ...table.getState().columnSizing,
        },
        width: table.getTotalSize(),
      },
    })
  );
};

export const deleteColumnSizing = (
  columnKey: string,
  tableKey: string = 'songs'
) => {
  const sizing = getColumnSizing();

  if (!sizing[tableKey]?.columnSizing) {
    return;
  }

  delete sizing[tableKey].columnSizing![columnKey];
  if (Object.keys(sizing[tableKey].columnSizing!).length === 0) {
    delete sizing[tableKey];
  }

  if (Object.keys(sizing).length === 0) {
    localStorage.removeItem('columnSizing');
    return;
  }

  localStorage.setItem('columnSizing', JSON.stringify(sizing));
};

export const getTableWidth = <T>(table: Table<T>, gapWidth?: number) =>
  gapWidth ? table.getTotalSize() + gapWidth : table.getTotalSize();

export const getColumnWidth = <T>(
  header: Header<T, unknown>,
  gapWidth?: number
) => {
  if (header.id !== 'gap') {
    return header.getSize();
  }

  const gap = Math.max(gapWidth || 0);
  return gap ?? 0;
};

export const useTotalWidth = (ref: React.RefObject<HTMLDivElement>) => {
  const [totalWidth, setTotalWidth] = useState<number | undefined>(
    ref.current?.clientWidth
  );
  useEffect(() => {
    if (!ref.current) return;
    const resizeObserver = new ResizeObserver(() => {
      setTotalWidth(ref.current?.clientWidth);
    });
    resizeObserver.observe(ref.current);
    // eslint-disable-next-line consistent-return
    return () => resizeObserver.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current?.clientWidth]);

  return totalWidth;
};
